import { LayaGL } from "../../layagl/LayaGL";
import { Color } from "../../maths/Color";
import { Vector4 } from "../../maths/Vector4";
import { BaseRenderNode2D } from "../../NodeRender2D/BaseRenderNode2D";
import { RenderState } from "../../RenderDriver/RenderModuleData/Design/RenderState";
import { Shader3D, ShaderFeatureType } from "../../RenderEngine/RenderShader/Shader3D";
import { Material } from "../../resource/Material";
import { VertexMesh2D } from "../../resource/Mesh2D";
import { Texture } from "../../resource/Texture";
import { ShaderDefines2D } from "../../webgl/shader/d2/ShaderDefines2D";
export class Mesh2DRender extends BaseRenderNode2D {
    static __init__() {
        if (Mesh2DRender.mesh2DDefaultMaterial)
            return;
        Mesh2DRender.mesh2DDefaultMaterial = new Material();
        Mesh2DRender.mesh2DDefaultMaterial.setShaderName("baseRender2D");
        Mesh2DRender.mesh2DDefaultMaterial.setBoolByIndex(Shader3D.DEPTH_WRITE, false);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.DEPTH_TEST, RenderState.DEPTHTEST_OFF);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND, RenderState.BLEND_ENABLE_ALL);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_EQUATION, RenderState.BLENDEQUATION_ADD);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ONE);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE_MINUS_SRC_ALPHA);
        Mesh2DRender.mesh2DDefaultMaterial.setFloatByIndex(ShaderDefines2D.UNIFORM_VERTALPHA, 1.0);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.CULL, RenderState.CULL_NONE);
        Mesh2DRender.mesh2DDefaultMaterial.lock = true;
    }
    _createRenderHandle() {
        return LayaGL.render2DRenderPassFactory.createMesh2DRenderDataHandle();
    }
    _initDefaultRenderData() {
        this.color = new Color();
        this.tilingOffset = new Vector4(0, 0, 1, 1);
        this.texture = null;
    }
    _isMaterialVaild(value) {
        return value.checkType(ShaderFeatureType.D2_BaseRenderNode2D);
    }
    renderUpdate(context) {
        this._updateLight();
    }
    set sharedMesh(value) {
        if (this._sharedMesh == value)
            return;
        let meshArrayDefine = new Array();
        if (this._sharedMesh) {
            VertexMesh2D.getMeshDefine(this._sharedMesh, meshArrayDefine);
            for (var i = 0, n = meshArrayDefine.length; i < n; i++)
                this._spriteShaderData.removeDefine(meshArrayDefine[i]);
            this._sharedMesh._removeReference();
        }
        meshArrayDefine.length = 0;
        if (value) {
            if (!value._vertexBuffers) {
                value = null;
                console.warn("not a 2D mesh");
            }
            else {
                VertexMesh2D.getMeshDefine(value, meshArrayDefine);
                for (var i = 0, n = meshArrayDefine.length; i < n; i++)
                    this._spriteShaderData.addDefine(meshArrayDefine[i]);
                value._addReference();
            }
        }
        this._sharedMesh = value;
        this._changeMesh();
    }
    get sharedMesh() {
        return this._sharedMesh;
    }
    set color(value) {
        this._renderHandle.baseColor = value;
    }
    get color() {
        return this._renderHandle.baseColor;
    }
    set tilingOffset(value) {
        this._tilingOffset = value;
        this._updateTilingOffset();
    }
    get tilingOffset() {
        return this._tilingOffset;
    }
    _updateTilingOffset() {
        let tilingOffset = this._renderHandle.tilingOffset;
        if (this._tilingOffset == null) {
            tilingOffset.setValue(this._textureTilingOffset.x, this._textureTilingOffset.y, this._textureTilingOffset.z, this._textureTilingOffset.w);
        }
        else {
            tilingOffset.setValue(this._tilingOffset.x * this._textureTilingOffset.z + this._textureTilingOffset.x, this._tilingOffset.y * this._textureTilingOffset.w + this._textureTilingOffset.y, this._tilingOffset.z * this._textureTilingOffset.z, this._tilingOffset.w * this._textureTilingOffset.w);
        }
        this._renderHandle.tilingOffset = tilingOffset;
    }
    set texture(value) {
        if (this._texture instanceof Texture) {
            this._texture._removeReference();
        }
        this._texture = value;
        if (value instanceof Texture) {
            value._addReference();
            if (value.uv !== Texture.DEF_UV) {
                console.warn("Texture uv is not default, it will affect the tiling offset.");
                let sx = value.uvrect[2] / value.width;
                let sy = value.uvrect[3] / value.height;
                this._textureTilingOffset.setValue(value.uvrect[0] - value.offsetX * sx, value.uvrect[1] - value.offsetY * sy, value.sourceWidth * sx, value.sourceHeight * sy);
            }
            else {
                this._textureTilingOffset.setValue(0, 0, 1, 1);
            }
            value = value.bitmap;
        }
        else {
            this._textureTilingOffset.setValue(0, 0, 1, 1);
        }
        this._updateTilingOffset();
        this._renderHandle.baseTexture = value;
    }
    get texture() {
        return this._texture;
    }
    set normalTexture(value) {
        this._renderHandle.normal2DTexture = value;
    }
    get normalTexture() {
        return this._renderHandle.normal2DTexture;
    }
    set normalStrength(value) {
        this._renderHandle.normal2DStrength = value;
    }
    get normalStrength() {
        return this._renderHandle.normal2DStrength;
    }
    set sharedMaterial(value) {
        super.sharedMaterial = value;
        this._changeMesh();
    }
    get sharedMaterial() {
        return this._materials[0];
    }
    _changeMesh() {
        let submeshNum = this._sharedMesh ? this._sharedMesh.subMeshCount : 0;
        if (submeshNum < this._renderElements.length) {
            for (var i = this._renderElements.length, n = submeshNum; n < i; i--) {
                let element = this._renderElements[i - 1];
                element.destroy();
            }
            this._renderElements.length = submeshNum;
        }
        for (var i = 0, n = submeshNum; i < n; i++) {
            let element = this._renderElements[i];
            if (!element)
                element = this._renderElements[i] = LayaGL.render2DRenderPassFactory.createRenderElement2D();
            element.geometry = this._sharedMesh.getSubMesh(i);
            element.value2DShaderData = this._spriteShaderData;
            BaseRenderNode2D._setRenderElement2DMaterial(element, this._materials[i] ? this._materials[i] : Mesh2DRender.mesh2DDefaultMaterial);
            element.renderStateIsBySprite = false;
            element.nodeCommonMap = this._getcommonUniformMap();
            element.owner = this._struct;
        }
        this._struct.renderElements = this._renderElements;
    }
    constructor() {
        super();
        this._textureTilingOffset = new Vector4(0, 0, 1, 1);
        this._renderElements = [];
        this._materials = [];
    }
}
